<script setup>
import { computed, onBeforeMount, watch } from 'vue';
import { PhxEngraving, PhxIcon } from '@phx-styleguides/jlc';
import { get } from 'lodash-es';
import EngravingToolCheckout from '@maison/components/engravingTool/checkout/EngravingToolCheckout.vue';
import PictureLazy from '@maison/components/common/PictureLazy';
import BackgroundVideo from '@maison/components/molecules/BackgroundVideo.vue';
import { useTotalPrice } from '@maison/composables/engravingTool/totalPrice';
import { useOptions } from '@maison/composables/engravingTool/options';
import { useValuesFromUrl } from '@maison/composables/engravingTool/valuesFromUrl';
import { useConfiguration } from '@maison/composables/engravingTool/configuration';
import { useReference } from '@maison/composables/engravingTool/reference';
import { useResult } from '@maison/composables/engravingTool/result';
import { useContent } from '@maison/composables/engravingTool/content';
import { useArticle } from '@maison/composables/engravingTool/article';
import { useSummary } from '@maison/composables/engravingTool/summary';
import { useView } from '@maison/composables/engravingTool/view';
import EngravingToolInactivityModal from '@maison/components/engravingTool/EngravingToolInactivityModal.vue';
import { useInactivity } from '@maison/composables/engravingTool/inactivity';
import EngravingToolSuccessModal from '@maison/components/engravingTool/EngravingToolSuccessModal.vue';
import { useStore } from 'vuex';
import { useTracking } from '@maison/composables/engravingTool/tracking';

const successModalId = 'engraving-tool-success-modal';

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  selectionModalId: {
    type: String,
    required: true,
  },
  allowedReferences: {
    type: Array,
    required: true,
  },
  returnUrl: {
    type: String,
    required: true,
  },
  homepageUrl: {
    type: String,
    required: true,
  },
  pdfPlaceholder: {
    type: String,
    required: true,
  },
  form: {
    type: Object,
    required: true,
  },
  matchConfigurationIds: {
    type: Object,
    required: true,
  },
  phoneNumbers: {
    type: Object,
    required: true,
  },
  backgroundMedia: {
    type: Object,
    default: null,
  },
  inactivityDelay: {
    type: Number,
    default: 0,
  },
  successModalRedirectDelay: {
    type: Number,
    default: 0,
  },
  enableScaleWarning: {
    type: Boolean,
    default: false,
  },
  enableMaterialWarning: {
    type: Boolean,
    default: false,
  },
});

const phoneNumber = computed(() => get(props.phoneNumbers, [window.Phoenix.currentCountry], null));

const { views, changeView, isCurrentView } = useView();
const { reference, referenceType, openChangeReferenceModal } = useReference(props);
const { configuration, filteredConfiguration } = useConfiguration();
const { options, formattedOptions, getOptionParam, getOptionItemParam } = useOptions(props);
const { formattedTotalPrice, calculateTotalPrice } = useTotalPrice(props, getOptionItemParam);
const { updateValuesFromUrlParams, updateUrlParamsFromValues } = useValuesFromUrl(
  reference,
  configuration,
  filteredConfiguration,
);
const { result, hasBespokeFile, setResultFromEngraving, setBespokeFile } = useResult(props, configuration);
const { content } = useContent(props, options, reference, configuration, phoneNumber, formattedTotalPrice);
const { article } = useArticle(
  props,
  getOptionParam,
  getOptionItemParam,
  configuration,
  reference,
  formattedTotalPrice,
  result,
);
const { summary } = useSummary(props, getOptionParam, getOptionItemParam, configuration, formattedTotalPrice);
const { trackingData } = useTracking(reference, configuration);
const store = useStore();

const shouldDisplayEngraving = computed(() => isCurrentView(views.DEFAULT) && reference.value !== null);
const shouldDisplayCheckout = computed(() => isCurrentView(views.CHECKOUT));

const hasBackgroundMedia = computed(() => props.backgroundMedia !== null);
const hasBackgroundImage = computed(() => hasBackgroundMedia.value && Object.hasOwn(props.backgroundMedia, 'image'));
const hasBackgroundVideo = computed(() => hasBackgroundMedia.value && Object.hasOwn(props.backgroundMedia, 'videoId'));

const hasStarted = computed(() => reference.value !== null || !store.getters['engravingToolModal/isDefaultView']);

const { inactivityModalId, resetInactivityDelay, disableInactivity } = useInactivity(hasStarted, props.inactivityDelay);

const onReset = async () => {
  window.location.href = props.returnUrl;
};

const onSubmit = async (options) => {
  if (!hasBespokeFile.value) {
    await setResultFromEngraving(options);
  }

  changeView(views.CHECKOUT);

  window.tracking.pushEvent(
    window.tracking.getEngravingEstimationFinalisedTracking(trackingData.value.itemId, trackingData.value.itemName),
  );
};

watch(reference, (value) => {
  if (value === null) {
    openChangeReferenceModal();
    return;
  }

  if (!props.allowedReferences.includes(value)) {
    reference.value = null;
    return;
  }

  updateUrlParamsFromValues();
});

watch(
  configuration,
  (value) => {
    calculateTotalPrice(value);
    updateUrlParamsFromValues();
  },
  { deep: true },
);

onBeforeMount(() => {
  updateValuesFromUrlParams();

  if (reference.value === null) {
    openChangeReferenceModal();
  }
});
</script>

<template>
  <phx-engraving
    v-if="shouldDisplayEngraving"
    v-model="configuration"
    :reference="reference"
    :content="content"
    :options="formattedOptions"
    :enable-scale-warning="enableScaleWarning"
    :enable-material-warning="enableMaterialWarning"
    @update:reference="() => openChangeReferenceModal({ isClosable: true })"
    @submit="onSubmit"
  >
    <template #view:actions>
      <a :href="returnUrl" class="btn btn--default btn--icon-only btn--icon btn--negative">
        <span class="sr-only">{{ $t('Accessibility.modal.close') }}</span>
        <span class="btn__icon">
          <phx-icon icon="cross-16" :title="$t('Accessibility.modal.close')" />
        </span>
      </a>
    </template>

    <template #view:switcher>
      <div class="engraving-view__mode-switcher">
        <button id="edit-mode-button" type="button" class="btn btn--default btn--sm btn--icon btn--alternative">
          <span class="btn__text">{{ $t('EngravingTool.mode.edit') }}</span>

          <span class="btn__icon">
            <phx-icon icon="edit-16" />
          </span>
        </button>

        <button id="view-mode-button" type="button" class="btn btn--default btn--sm btn--icon btn--alternative">
          <span class="btn__text">{{ $t('EngravingTool.mode.view') }}</span>

          <span class="btn__icon">
            <phx-icon icon="box-16" />
          </span>
        </button>
      </div>
    </template>

    <template #view:bespoke>
      <div class="engraving-bespoke">
        <div v-if="hasBespokeFile" class="engraving-bespoke-preview">
          <div class="engraving-bespoke-preview__header">
            <h2 class="h4">{{ $t('EngravingTool.bespoke.previewTitle') }}</h2>

            <button
              type="button"
              class="btn btn--default btn--negative btn--icon btn--icon-only btn--sm"
              @click="setBespokeFile(null)"
            >
              <span class="btn__icon">
                <phx-icon icon="cross-16" />
              </span>
            </button>
          </div>

          <div class="engraving-bespoke-preview__media">
            <div class="picture">
              <picture-lazy :image="{ src: result.previewUrl, srcset: result.previewUrl }" />
            </div>
          </div>
        </div>

        <template v-else>
          <h2 class="h3" v-html="$t('EngravingTool.bespoke.dragAndDrop')" />

          <div class="text-sm text-muted">{{ $t('EngravingTool.bespoke.or') }}</div>

          <div class="form-field">
            <label for="engraving-bespoke-input" class="engraving-bespoke__label">
              <div class="btn btn--default btn--icon btn--negative">
                <span class="btn__text">{{ $t('EngravingTool.bespoke.label') }}</span>
                <span class="btn__icon">
                  <phx-icon icon="upload-16" />
                </span>
              </div>
            </label>

            <ul class="field-help">
              <li>{{ $t('EngravingTool.bespoke.help1') }}</li>
              <li>{{ $t('EngravingTool.bespoke.help2') }}</li>
              <li>{{ $t('EngravingTool.bespoke.help3') }}</li>
            </ul>

            <input
              type="file"
              accept="image/*,.pdf"
              class="engraving-bespoke__input expand-target"
              @change="setBespokeFile($event.target.files[0])"
            />
          </div>
        </template>
      </div>
    </template>
  </phx-engraving>

  <engraving-tool-checkout
    v-else-if="shouldDisplayCheckout"
    :homepage-url="homepageUrl"
    :reference="reference"
    :reference-type="referenceType"
    :configuration="filteredConfiguration"
    :article="article"
    :summary="summary"
    :form="form"
    :result-file="result.file"
    :result-preview="result.preview"
    :phone-number="phoneNumber"
    :tracking-data="trackingData"
    :success-modal-id="successModalId"
    @back="changeView(views.DEFAULT)"
  />

  <div v-else-if="hasBackgroundImage" class="picture picture--cover">
    <picture-lazy v-bind="backgroundMedia" />
  </div>

  <background-video v-else-if="hasBackgroundVideo" v-bind="backgroundMedia" />

  <engraving-tool-inactivity-modal
    v-if="inactivityDelay"
    :id="inactivityModalId"
    @close="resetInactivityDelay()"
    @reset="onReset()"
  />

  <engraving-tool-success-modal
    v-if="!form.redirectUrl"
    :id="successModalId"
    :reset-delay="successModalRedirectDelay"
    :on-show="disableInactivity"
    @reset="onReset()"
  />
</template>

<style>
.engraving-bespoke-preview {
  max-width: 500px;
}

.grecaptcha-badge {
  visibility: hidden;
}
</style>
